import { Button, Divider, Flex, Input, LoadingOverlay, Modal } from "@mantine/core";
import { IconEdit, IconSearch, IconTrash } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { categoryApi } from "../../apis";
import { ICategory } from "../../interfaces/ICategory";
import { Dialog } from "../../utils/confirm-modal";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { PageLayout } from "../layout";
import { ColumnType, ITableRef, Table } from "../table";
import { Tag } from "../tags";
import RenderText from "../ui/render-text";
import { SubClassificationForm } from "./sub-classification-form";

export function SubClassificationList() {
  const [action, setAction] = useState<string[]>([]);
  const ref = useRef<ITableRef>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<string>("");

  const columns = useHeader({
    onClick: (key, item) => {
      switch (key) {
        case "edit": {
          setAction(["update", item as any]);
          break;
        }

        case "remove": {
          Dialog.confirm("Та үүнийг устгахдаа итгэлтэй байна уу?", async (key) => {
            switch (key) {
              case "confirm": {
                setLoading(true);
                try {
                  let res = await categoryApi.remove(item.id);
                  if (res.success) {
                    setTimeout(() => {
                      ref.current?.reload();
                    }, 200);
                  }
                  Message.success("Амжилттай дэд ангиллыг устгалаа!");
                  setLoading(false);
                } catch (err) {
                  setLoading(false);
                  Message.error((err as HttpHandler)?.message!);
                }
                break;
              }

              default:
            }
          });
          break;
        }
      }
    },
  });

  const onCancel = async () => {
    setAction([]);
  };

  const reload = () => {
    ref.current?.reload();
  };

  if (loading) return <LoadingOverlay visible={loading} />;

  return (
    <>
      <PageLayout
        title="Дэд ангилал"
        subTitle="Бараа бүтээгдэхүүн, ажил үйлчилгээний дэд ангилалийн жагсаалт"
        extra={[
          <Input leftSection={<IconSearch />} placeholder="Нэрээр хайх" onChange={(e) => setQuery(e.currentTarget.value)} />,
          <Button key={1} variant="default" onClick={() => navigate("/reference/product-list")}>
            Буцах
          </Button>,
          <Button
            key={2}
            onClick={() => {
              setAction(["new"]);
            }}>
            Нэмэх
          </Button>,
        ]}>
        <Divider my={10} />
        <Table
          ref={ref}
          name="sub-classificaiton.list"
          columns={columns}
          filters={{ query: query, type: "SUB_CLASSIFICATION" }}
          loadData={(data) => categoryApi.list(data!)}
        />
      </PageLayout>
      <Modal opened={!!action[0]} onClose={() => setAction([])} withCloseButton={false} centered size="md">
        <SubClassificationForm onCancel={onCancel} action={action} reload={reload} />
      </Modal>
    </>
  );
}

const useHeader = ({ onClick }: { onClick: (key: string, record: ICategory) => void }): ColumnType<ICategory>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Flex gap="sm">
          <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("edit", record)}>
            <IconEdit />
          </Button>
          <Button variant="light" color="red" radius={100} w={35} h={35} p={0} onClick={() => onClick("remove", record)}>
            <IconTrash />
          </Button>
        </Flex>
      );
    },
  },
  {
    title: "Нэр",
    dataIndex: "name",
    render: (record) => <RenderText text={record?.name ?? "-"} />,
  },
  {
    title: "Код",
    dataIndex: "refCode",
    render: (record) => <RenderText text={record?.refCode ?? "-"} />,
  },
  {
    title: "Нэр төрөл",
    dataIndex: "name",
    render: (record) => <RenderText text={record?.itemType?.name ?? "-"} />,
  },
  {
    title: "Ангилал",
    dataIndex: "name",
    render: (record) => <RenderText text={record?.classification?.name ?? "-"} />,
  },
  {
    title: "Тайлбар",
    dataIndex: "description",
    render: (record) => <RenderText text={record?.description ?? "-"} />,
  },
  {
    title: "Нээлттэй эсэх",
    dataIndex: "isPublic",
    render: (record) => <Tag name={record.isPublic ? "Тийм" : "Үгүй"} color={record.isPublic ? "green.5" : "red.5"} />,
  },
  {
    title: "Бараа эсэх",
    dataIndex: "isGoods",
    render: (record) => <Tag name={record.isGoods ? "Тийм" : "Үгүй"} color={record.isGoods ? "green.5" : "red.5"} />,
  },
  {
    title: "Үйлчилгээ эсэх",
    dataIndex: "isService",
    render: (record) => <Tag name={record.isService ? "Тийм" : "Үгүй"} color={record.isService ? "green.5" : "red.5"} />,
  },
];
